import { useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { candidaciesMinimalQueryKey } from "@/hooks/queries/useGetCandidaciesMinimal";
import { TERMINAL_CANDIDACY_STATUSES } from "@/lib/constants";
import {
  LastPing,
  MinimalCandidacy,
  MinimalCandidacyStatusChange,
  MinimalScore,
  MyCandidacyResponse,
  VoiceAIOutreachSlim,
} from "@/types";

// Updates a candidacy across candidacies-minimal (pipeline), my-candidacies
// (My Aviary) without refetching, refetches for dashboard cache
export const useSetCandidacyInAllLists = () => {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  return ({
    candidacyId,
    deferDashboardInvalidate = false,
    lastCandidacyStatusChange,
    location,
    newAgenticScore,
    newLastPing,
    owners,
    stafferAgentActive,
    voiceAiOutreach,
  }: {
    candidacyId: string;
    // The review portal actions dozens of candidacies in a row; refetching the
    // whole dashboard per decision is the expensive part, so it batches one
    // invalidate on close instead.
    deferDashboardInvalidate?: boolean;
    lastCandidacyStatusChange?: MinimalCandidacyStatusChange;
    location?: string | null;
    newAgenticScore?: MinimalScore;
    newLastPing?: LastPing;
    owners?: MinimalCandidacy["owners"];
    stafferAgentActive?: boolean;
    voiceAiOutreach?: VoiceAIOutreachSlim | null;
  }) => {
    queryClient.setQueriesData(
      { queryKey: candidaciesMinimalQueryKey(currentCompanyId, currentJobId) },
      (cachedData?: MinimalCandidacy[]): MinimalCandidacy[] | undefined => {
        if (!cachedData) return cachedData;

        return cachedData.map((minimalCandidacy: MinimalCandidacy) =>
          minimalCandidacy.id === candidacyId
            ? {
                ...minimalCandidacy,
                ...(lastCandidacyStatusChange !== undefined && {
                  lastCandidacyStatusChange,
                }),
                ...(location !== undefined && { location }),
                ...(newAgenticScore !== undefined && {
                  agenticScores: [
                    newAgenticScore,
                    ...minimalCandidacy.agenticScores,
                  ],
                }),
                ...(owners !== undefined && { owners }),
                ...(stafferAgentActive !== undefined && {
                  stafferAgentActive,
                }),
                ...(voiceAiOutreach !== undefined && { voiceAiOutreach }),
              }
            : minimalCandidacy,
        );
      },
    );

    queryClient.setQueriesData(
      { queryKey: ["my-candidacies"] },
      (
        cachedData?: MyCandidacyResponse[],
      ): MyCandidacyResponse[] | undefined => {
        if (!cachedData) return cachedData;
        return cachedData.map((row) => {
          if (row.candidacyId !== candidacyId) return row;
          return {
            ...row,
            ...(lastCandidacyStatusChange !== undefined && {
              status: lastCandidacyStatusChange.toStatus,
            }),
            ...(newAgenticScore !== undefined && {
              agenticScores: [newAgenticScore, ...row.agenticScores],
            }),
            ...(newLastPing !== undefined && { lastPing: newLastPing }),
          };
        });
      },
    );

    // A restored terminal candidate may be absent from the active-only cache.
    if (
      lastCandidacyStatusChange?.fromStatus &&
      TERMINAL_CANDIDACY_STATUSES.includes(
        lastCandidacyStatusChange.fromStatus,
      ) &&
      !TERMINAL_CANDIDACY_STATUSES.includes(lastCandidacyStatusChange.toStatus)
    ) {
      void queryClient.invalidateQueries({
        queryKey: candidaciesMinimalQueryKey(currentCompanyId, currentJobId),
      });
    }

    if (lastCandidacyStatusChange !== undefined && !deferDashboardInvalidate) {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      // Separate key root, so the ["dashboard"] invalidate above never matches
      // it — an actioned candidate lingers in Top Candidates without this.
      void queryClient.invalidateQueries({
        queryKey: ["dashboard-top-candidates"],
      });
    }
  };
};
